import { useProfile } from '@app/app/_context/ProfileContext/useProfile';
import { Address } from '@app/domain/model/Address';
import { VehicleOwnerFormData } from '@app/domain/model/eshop/transport/VehicleOwnerFormData';
import { TransportInsuranceRequestResult } from '@app/service/eshop/transport/actions/requestTransportInsurance';
import { useCallback, useEffect, useState } from 'react';
import { formatLithuanianPhone } from '@app/lib/phone/formatLithuanianPhone';
import { extractLithuanianLocalNumber } from '@app/lib/phone/extractLithuanianLocalNumber';

export const useOwnerFormFields = (requestResult: TransportInsuranceRequestResult, isCompany: boolean) => {
    const profile = useProfile();

    const getInitialOwnerAddress = useCallback((): Address | null => {
        const profileAddress = profile?.mainAddress || profile?.addresses[0];

        if (!profileAddress) {
            return null;
        }

        const hasRequiredFields =
            profileAddress.country &&
            profileAddress.street &&
            profileAddress.houseNumber &&
            profileAddress.city &&
            profileAddress.postalCode &&
            profileAddress.municipality;

        return hasRequiredFields ? profileAddress : null;
    }, [profile]);

    const isOwnerLoggedInUser = profile?.personalCode === requestResult.personalCode;

    const getOwnerFirstName = useCallback(() => {
        return isCompany ? (profile?.name ?? '') : (profile?.firstName ?? '');
    }, [isCompany, profile?.name, profile?.firstName]);

    const getOwnerLastName = useCallback(() => {
        return isCompany ? '' : (profile?.name ?? '');
    }, [isCompany, profile?.name]);

    const getOwnerEmail = useCallback(() => {
        return profile?.mainEmail ?? profile?.emails?.[0]?.email ?? '';
    }, [profile?.mainEmail, profile?.emails]);

    const [ownerFormData, setOwnerFormData] = useState<VehicleOwnerFormData>({
        ownerFirstName: isOwnerLoggedInUser ? getOwnerFirstName() : '',
        ownerLastName: isOwnerLoggedInUser ? getOwnerLastName() : '',
        ownerPersonalCode: requestResult.personalCode,
        phoneNumber: isOwnerLoggedInUser
            ? formatLithuanianPhone(extractLithuanianLocalNumber(profile?.mainPhone ?? profile?.phones?.[0]?.phone))
            : '',
        email: isOwnerLoggedInUser ? getOwnerEmail() : '',
        emailConfirmation: '',
        ownerAddress: isOwnerLoggedInUser ? getInitialOwnerAddress() : null,
        insuredByOwner: true,
        payerFirstName: '',
        payerLastName: '',
        payerPersonalCode: '',
        payerPhoneNumber: '',
        payerEmail: '',
        payerEmailConfirmation: '',
        payerAddress: null,
    });

    useEffect(() => {
        if (profile) {
            const isOwner = profile.personalCode === requestResult.personalCode;

            setOwnerFormData(prev => ({
                ...prev,
                ownerFirstName: isOwner ? getOwnerFirstName() : prev.ownerFirstName,
                ownerLastName: isOwner ? getOwnerLastName() : prev.ownerLastName,
                ownerPersonalCode: requestResult.personalCode,
                phoneNumber: isOwner
                    ? formatLithuanianPhone(
                          extractLithuanianLocalNumber(profile.mainPhone ?? profile.phones?.[0]?.phone),
                      )
                    : prev.phoneNumber,
                ownerAddress: isOwner ? getInitialOwnerAddress() : prev.ownerAddress,
                email: isOwner ? getOwnerEmail() : prev.email,
                insuredByOwner: true,
                payerFirstName: '',
                payerLastName: '',
                payerPersonalCode: '',
                payerPhoneNumber: '',
                payerEmail: '',
                payerAddress: null,
            }));
        }
    }, [
        getInitialOwnerAddress,
        profile,
        requestResult.personalCode,
        getOwnerFirstName,
        getOwnerLastName,
        getOwnerEmail,
    ]);

    const handleInputChange = useCallback((field: keyof VehicleOwnerFormData, value: string | boolean | null) => {
        setOwnerFormData(prev => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const onChangeOwnerAddress = useCallback((address: Address | null) => {
        setOwnerFormData(prev => ({
            ...prev,
            ownerAddress: address,
        }));
    }, []);

    const onChangePayerAddress = useCallback((address: Address | null) => {
        setOwnerFormData(prev => ({
            ...prev,
            payerAddress: address,
        }));
    }, []);

    const handlePhoneNumberField = useCallback(
        (field: 'phoneNumber' | 'payerPhoneNumber') => {
            return (event: React.ChangeEvent<HTMLInputElement>) => {
                const formattedValue = formatLithuanianPhone(event.target.value);
                handleInputChange(field, formattedValue);
            };
        },
        [handleInputChange],
    );

    const handleInputField = useCallback(
        (field: keyof VehicleOwnerFormData) => {
            return (event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
                handleInputChange(field, value);
            };
        },
        [handleInputChange],
    );

    const updateFormData = useCallback((updates: Partial<VehicleOwnerFormData>) => {
        setOwnerFormData(prev => ({ ...prev, ...updates }));
    }, []);

    const resetForm = useCallback(() => {
        const isOwner = profile?.personalCode === requestResult.personalCode;

        setOwnerFormData({
            ownerFirstName: isOwner ? getOwnerFirstName() : '',
            ownerLastName: isOwner ? getOwnerLastName() : '',
            ownerPersonalCode: requestResult.personalCode ?? '',
            phoneNumber: isOwner
                ? formatLithuanianPhone(
                      extractLithuanianLocalNumber(profile?.mainPhone ?? profile?.phones?.[0]?.phone),
                  )
                : '',
            email: isOwner ? getOwnerEmail() : '',
            emailConfirmation: '',
            ownerAddress: isOwner ? getInitialOwnerAddress() : null,
            insuredByOwner: true,
            payerFirstName: '',
            payerLastName: '',
            payerPersonalCode: '',
            payerPhoneNumber: '',
            payerEmail: '',
            payerEmailConfirmation: '',
            payerAddress: null,
        });
    }, [
        profile,
        requestResult,
        getInitialOwnerAddress,
        getOwnerFirstName,
        getOwnerLastName,
        getOwnerEmail,
    ]);

    return {
        ownerFormData,

        handleInputField,
        handlePhoneNumberField,
        handleInputChange,
        updateFormData,
        resetForm,
        setOwnerFormData,

        onChangeOwnerAddress,
        onChangePayerAddress,
    };
};
